/**
 * When a booked visit actually happened — the scan-in and scan-out times the
 * history list shows next to the window that was booked.
 *
 * The door opens ONE session per visit and binds it to ONE booking: the
 * earliest of that day, private room first (see _zz_open_session). Two things
 * follow, and both used to leave the in/out columns empty while walk-ins —
 * whose scanned window IS the visit — always showed times:
 *
 *   · A stack booked as five rows has a session on row one and nothing on rows
 *     two to five, even though all five are one visit (they share a charge
 *     group and a single charge).
 *   · A member who scanned in more than five minutes before their booking
 *     starts opens a session bound to no booking at all.
 *
 * So a booking is matched to a session three ways, in order of confidence:
 * the session bound to that exact row, then any session bound to a row in the
 * same charge group, then any session of theirs whose time overlaps the booked
 * window on the day. The clock is the last resort, but it is the same rule
 * _zz_unbooked_credit_units already applies when it decides which of a
 * walk-in's minutes were already paid for.
 */

/** A member's entry session, as stored (instants, UTC). */
export type VisitSession = {
  bookingId: string | null;
  startedAt: string;
  endedAt: string | null;
};

/** The booking row being matched — a naive SAST date + times, as stored. */
export type VisitBooking = {
  id: string;
  charge_group_id?: string | null;
  booking_date: string;
  start_time: string;
  end_time: string;
};

export type VisitTimes = { scanIn: string; scanOut: string | null };

/** An instant as SAST "HH:MM" — what members read everywhere in the app. */
export const sastTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString("en-ZA", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Africa/Johannesburg",
  });

/** An instant as its SAST calendar date, "YYYY-MM-DD". */
const sastDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-CA", {
    timeZone: "Africa/Johannesburg",
  });

const minutesOfDay = (hhmm: string): number => {
  const [h, m] = (hhmm || "").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

type Span = VisitSession & {
  group: string | null;
  date: string;
  startMin: number;
  endMin: number;
};

/**
 * Normalise sessions for matching. `groupOf` maps a booking id to its charge
 * group (a booking with no group is its own group), so a session bound to one
 * row of a stack can be recognised as belonging to all of them.
 */
export function buildVisitSessions(
  rows: VisitSession[],
  groupOf: Record<string, string>,
): Span[] {
  return rows.map((s) => ({
    ...s,
    group: s.bookingId ? (groupOf[s.bookingId] ?? null) : null,
    date: sastDate(s.startedAt),
    startMin: minutesOfDay(sastTime(s.startedAt)),
    // A session still open has no scan-out yet: treat it as running to the end
    // of the day so it still matches the window it's sitting inside.
    endMin: s.endedAt ? minutesOfDay(sastTime(s.endedAt)) : 24 * 60,
  }));
}

/** First scan-in to last scan-out across every session of one visit. */
function spanOf(matches: Span[]): VisitTimes | null {
  if (!matches.length) return null;
  let firstIn = matches[0].startedAt;
  let lastOut = matches[0].endedAt;
  matches.forEach((m) => {
    if (m.startedAt < firstIn) firstIn = m.startedAt;
    if (m.endedAt && (!lastOut || m.endedAt > lastOut)) lastOut = m.endedAt;
  });
  return {
    scanIn: sastTime(firstIn),
    scanOut: lastOut ? sastTime(lastOut) : null,
  };
}

/**
 * The real in/out times for a booking, or null if it was never walked into.
 * A visit that spans several sessions (scan in, step out, scan back in) reads
 * as one window: the first way in to the last way out.
 */
export function visitTimesFor(
  sessions: Span[],
  booking: VisitBooking,
): VisitTimes | null {
  const exact = sessions.filter((s) => s.bookingId === booking.id);
  if (exact.length) return spanOf(exact);

  const group = booking.charge_group_id || booking.id;
  const sameGroup = sessions.filter((s) => s.group === group);
  if (sameGroup.length) return spanOf(sameGroup);

  const startMin = minutesOfDay(booking.start_time);
  const endMin = minutesOfDay(booking.end_time);
  return spanOf(
    sessions.filter(
      (s) =>
        s.date === booking.booking_date &&
        s.startMin < endMin &&
        s.endMin > startMin,
    ),
  );
}
